import { lazy, Suspense, useState, useEffect, useMemo } from 'react';
import { ReviewedBy } from "@/components/ReviewedBy";
import { DynamicDetailedLoanCard } from "@/components/DynamicDetailedLoanCard";
import { SEO } from "@/components/SEO";
import { DynamicTrustpilotRating } from "@/components/DynamicTrustpilotRating";
import { LenderLiveDataStrip } from "@/components/LenderLiveDataStrip";
import { isUsableLiveTerms, useProviderLiveTermsMap } from "@/hooks/useProviderLiveTerms";
import { CheckCircle2, Info, AlertTriangle, BookOpen, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { CalculatorSkeleton } from "@/components/calculators/CalculatorSkeleton";
import { DeferredSection } from "@/components/DeferredSection";
const BusinessLoanCalculator = lazy(() => import("@/components/calculators/BusinessLoanCalculator").then(m => ({ default: m.BusinessLoanCalculator })));
const Footer = lazy(() => import("@/components/Footer"));
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RiskWarning } from "@/components/RiskWarning";
import { Button } from "@/components/ui/button";
import { StickyCTA } from "@/components/StickyCTA";
import { useStickyCTA } from "@/hooks/useStickyCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { ComparisonMethodology } from "@/components/content/ComparisonMethodology";
import { DecisionPathLinks } from "@/components/content/DecisionPathLinks";
import { generateComparisonPageSchema } from "@/utils/schemaGenerators";
import { applyLiveTermsToComparisonItems, getLatestLiveTermsUpdatedAt, getLiveTermsVerifiedLabel } from "@/utils/providerLiveTerms";
// Lazy load provider logos
const useProviderLogos = () => {
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sources: Record<string, () => Promise<{ default: string }>> = {
      krea: () => import("@/assets/krea-logo.webp"),
      lendo: () => import("@/assets/Lendo_stor.webp"),
      froda: () => import("@/assets/Froda_stor.webp"),
      qred: () => import("@/assets/qred_stor_compressed.webp"),
      fedelta: () => import("@/assets/Fedelta_stor.webp"),
      opr: () => import("@/assets/opr-foretagslan-logo_compressed.webp"),
    };

    const loadLogos = async () => {
      // Per-logo try/catch so one failed image can't freeze every logo on the
      // placeholder (and never leave `loading` stuck true).
      const entries = await Promise.all(
        Object.entries(sources).map(async ([key, importer]) => {
          try {
            return [key, (await importer()).default] as const;
          } catch {
            return [key, ""] as const;
          }
        })
      );
      setLogos(Object.fromEntries(entries));
      setLoading(false);
    };

    loadLogos();
  }, []);

  return { logos, loading };
};

const businessLoanLiveSlugs = ["krea", "lendo", "froda", "qred", "fedelta", "opr-foretagslan"];

const businessLoanSchemaItems = [
  {
    name: "Krea företagslån",
    provider: "Krea",
    url: "/sv-SE/foretagslan/krea",
    description: "Låneförmedlare som jämför företagslån från flera banker och långivare.",
    amount: "10 000 - 30 000 000 kr",
    interestRate: "Varierar mellan långivare",
    term: "1 - 60 månader",
    creditCheck: "UC",
  },
  {
    name: "Lendo företagslån",
    provider: "Lendo",
    url: "/sv-SE/foretagslan/lendo",
    description: "Jämförelse av företagslån från flera långivare med en ansökan.",
    amount: "50 000 - 5 000 000 kr",
    interestRate: "Varierar mellan långivare",
    term: "6 - 60 månader",
    creditCheck: "UC",
  },
  {
    name: "Froda företagslån",
    provider: "Froda",
    url: "/sv-SE/foretagslan/froda",
    description: "Digital företagsfinansiering med snabb handläggning.",
    amount: "Upp till 5 000 000 kr",
    interestRate: "12,7 - 29,3 % effektiv ränta",
    term: "Flexibel",
    creditCheck: "Alternativ kreditupplysning",
  },
  {
    name: "Qred företagslån",
    provider: "Qred",
    url: "/sv-SE/foretagslan/qred",
    description: "Företagslån och företagskredit för mindre företag.",
    amount: "Upp till 5 000 000 kr",
    interestRate: "19,6 - 43,3 % effektiv ränta",
    term: "Flexibel",
    creditCheck: "UC",
  },
  {
    name: "Fedelta företagslån",
    provider: "Fedelta",
    url: "/sv-SE/foretagslan/fedelta",
    description: "Företagslån för aktiebolag och etablerade verksamheter.",
    amount: "50 000 - 5 000 000 kr",
    interestRate: "11,4 - 18,1 % effektiv ränta",
    term: "6 - 60 månader",
    creditCheck: "UC",
  },
  {
    name: "OPR Företagslån",
    provider: "OPR Företagslån",
    url: "/sv-SE/foretagslan/opr-foretagslan",
    description: "Företagslån med digital ansökan och flexibel löptid.",
    amount: "Upp till 1 000 000 kr",
    interestRate: "22,4 - 31,2 % effektiv ränta",
    term: "Flexibel",
    creditCheck: "Alternativ kreditupplysning",
  },
];

const businessLoanSchemaBase = {
  name: "Jämför företagslån från flera långivare",
  description: "Klarfinans jämför företagslån, företagskrediter och låneförmedlare med fokus på belopp, löptid, ränta och krav.",
  url: "/sv-SE/foretagslan",
  category: "Företagslån",
  breadcrumbs: [
    { name: "Start", url: "https://klarfinans.se" },
    { name: "Företagslån" },
  ],
};

const BusinessLoanPage = () => {
  const { currentLanguage } = useLanguage();
  const { logos, loading: logosLoading } = useProviderLogos();
  const { data: liveTermsByBusinessSlug = {} } = useProviderLiveTermsMap("foretagslan", businessLoanLiveSlugs);
  const kreaLiveTerms = isUsableLiveTerms(liveTermsByBusinessSlug.krea) ? liveTermsByBusinessSlug.krea : null;
  const kreaVerifiedLabel = getLiveTermsVerifiedLabel(kreaLiveTerms);
  const showStickyCTA = useStickyCTA();
  const businessLoanSchemas = useMemo(
    () => generateComparisonPageSchema({
      ...businessLoanSchemaBase,
      items: applyLiveTermsToComparisonItems(businessLoanSchemaItems, liveTermsByBusinessSlug),
      updatedAt: getLatestLiveTermsUpdatedAt(liveTermsByBusinessSlug),
    }),
    [liveTermsByBusinessSlug]
  );

  return (
    <div className="min-h-screen">
      <SEO 
        title="Företagslån - Jämför företagslån från 30+ långivare"
        description="Jämför företagslån från över 30 långivare. Lån från 10 000 till 30 000 000 kr. Svar inom 24h och flexibel finansiering utan säkerhet."
        keywords={["företagslån", "företagskredit", "lån till företag", "blancolån företag", "finansiering aktiebolag"]}
        schemaData={businessLoanSchemas}
      />

      <section className="relative overflow-hidden text-white pt-20 pb-8 md:pt-24 md:pb-12 lg:pt-28 lg:pb-16" style={{ backgroundColor: "hsl(var(--primary-dark))" }}>
        <div className="container mx-auto px-3 md:px-4 relative z-10">
          <div className="grid gap-6 md:gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start lg:gap-14">
            <div className="space-y-4 md:space-y-5 lg:space-y-6">
              <Badge variant="outline" className="border-white/25 bg-white/10 text-white">
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                Handplockade långivare
              </Badge>
              <h1 className="max-w-3xl text-3xl md:text-5xl lg:text-6xl lg:leading-[1.05] lg:tracking-tight font-bold leading-tight text-balance">
                Jämför finansiering för företag
              </h1>
              <p className="max-w-2xl text-base md:text-lg leading-7 text-white/90">
                Vi listar inte alla – vi har handplockat de bästa företagslångivarna och förmedlarna åt dig. Se belopp, villkor och krav innan du går vidare.
              </p>

              <div className="grid max-w-2xl grid-cols-3 divide-x divide-white/20 rounded-lg border border-white/20 bg-white/10">
                {[
                  ["Gratis", "att jämföra"],
                  ["30 Mkr", "maxbelopp"],
                  ["24h", "vanligt svar"],
                ].map(([value, label]) => (
                  <div key={label} className="px-3 py-3">
                    <p className="whitespace-nowrap text-base font-bold leading-tight text-white sm:text-lg">{value}</p>
                    <p className="mt-1 text-xs text-white/75">{label}</p>
                  </div>
                ))}
              </div>

              <div className="hero-cta-section pt-2 max-w-sm">
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-white text-slate-950 hover:bg-white/90 shadow-none text-sm sm:text-base px-4"
                >
                  <a href="#compare-loans-section">
                    Se företagslån
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="max-w-lg mx-auto lg:mx-0">
              <div className="space-y-3 md:space-y-4">
                <div className="text-center mb-6 md:mb-4">
                  <h2 className="text-lg md:text-xl font-bold text-white">Förmedlare i fokus</h2>
                  <p className="text-white/80 text-sm">{kreaVerifiedLabel}</p>
                </div>

                <Card className="p-4 md:p-5 space-y-3 md:space-y-4 bg-card border border-primary/20 shadow-lg">
                  <span className="inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    Jämför 30 långivare
                  </span>
                  
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      {!logosLoading && logos.krea && (
                        <img src={logos.krea} alt="Krea" className="h-10 sm:h-12 md:h-14 w-auto object-contain max-w-full" />
                      )}
                    </div>
                    <DynamicTrustpilotRating
                      entityType="foretagslan"
                      entitySlug="krea"
                      fallbackRating={4.8}
                      fallbackReviewCount={491}
                      size="md"
                      showScore={true}
                      showLogo={true}
                    />
                    <LenderLiveDataStrip
                      entityType="foretagslan"
                      entitySlug="krea"
                      className="justify-center"
                      liveTermsOverride={liveTermsByBusinessSlug.krea ?? null}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:gap-4 py-3 md:py-4 border-y border-border">
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground">Effektiv ränta</p>
                      <p className="font-bold text-primary text-2xl md:text-3xl">
                        {kreaLiveTerms?.effective_rate_label || kreaLiveTerms?.nominal_rate_label || "från 11,4%"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground">Lånebelopp</p>
                      <p className="text-sm font-semibold text-foreground mt-1">
                        {kreaLiveTerms?.loan_amount_label || "10 000 – 30 000 000 kr"}
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {[
                      "En ansökan till flera företagslångivare",
                      "Svar inom 24 timmar från flera långivare",
                      "Kostnadsfri jämförelse"
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-3">
                    <Button asChild size="lg" className="w-full bg-primary hover:bg-primary-dark">
                      <a href="https://go.adt253.net/t/t?a=1301012751&as=2024680921&t=2&tk=1" target="_blank" rel="sponsored nofollow noopener noreferrer">
                        Ansök hos Krea
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                    
                    <Button asChild size="lg" variant="outline" className="w-full border border-muted-foreground/30 text-foreground bg-transparent hover:bg-transparent hover:text-foreground hover:border-muted-foreground/50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 group">
                      <Link to={`/${currentLanguage}/foretagslan/krea`} className="no-underline">
                        Läs mer om Krea
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>

                  <RiskWarning />
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vad är företagslån */}
      <section className="py-8 md:py-12 lg:py-16 bg-muted/30">
        <div className="container max-w-4xl px-3 md:px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Vad är ett företagslån?</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-muted-foreground text-lg mb-6">
              Ett företagslån är en finansieringslösning där ditt företag lånar kapital från en bank eller annan
              långivare för att stärka och utveckla verksamheten. Med ett företagslån ökar ni bolagets likviditet utan
              att du och de andra delägarna behöver minska ert aktieinnehav – på så sätt behåller ni kontrollen över
              företaget samtidigt som ni får värdefull likviditet.
            </p>

            <Card className="mb-2 md:mb-3 border-primary/20 shadow-lg bg-gradient-to-br from-card via-card to-primary/5">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  Vanliga användningsområden
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-1 w-8 bg-primary rounded-full"></div>
                      <h4 className="font-bold text-lg">Expansion & Tillväxt</h4>
                    </div>
                    <ul className="space-y-2.5 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Maskiner och utrustning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Öppna nya kontor</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Rekrytera personal</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Produktutveckling</span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-1 w-8 bg-primary rounded-full"></div>
                      <h4 className="font-bold text-lg">Drift & Likviditet</h4>
                    </div>
                    <ul className="space-y-2.5 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Varulager och lager</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Täcka säsongsvariationer</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Överbryggningsfinansiering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>Refinansiera andra lån</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section id="compare-loans-section" className="pt-4 md:pt-6 lg:pt-8 pb-8 md:pb-12 lg:pb-16 bg-background">
        <div className="container pl-safe-left pr-safe-right max-w-6xl mx-auto">
          <div className="mb-6 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Vårt urval</p>
            <h2 className="mt-1 text-2xl md:text-3xl font-bold">Handplockade företagslångivare och förmedlare</h2>
            <p className="mt-2 text-sm md:text-base text-muted-foreground">
              Vi presenterar inte alla på marknaden – bara de vi själva skulle välja. Ränta, krav och återbetalningstid varierar med företagets ekonomi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 1. Krea */}
            <div className="w-full lg:max-w-sm">
            <DynamicDetailedLoanCard
              provider="Krea"
              logo={!logosLoading ? logos.krea : "/placeholder.svg"}
              loanAmount="10 000 – 30 000 000 kr"
              nominalRate="Varierar mellan långivare"
              effectiveRate="Varierar mellan långivare"
              loanTerm="1 – 60 mån"
              loanWithoutUC="Nej"
              responseTime="Inom 24h"
              paymentSolution="Banköverföring"
              link={`/${currentLanguage}/foretagslan/krea`}
              lenderBadge="Jämför 30 långivare"
              entityType="foretagslan"
              entitySlug="krea"
              fallbackRating={4.8}
              fallbackReviewCount={491}
              liveTermsOverride={liveTermsByBusinessSlug.krea ?? null}
            />
            </div>

            {/* 2. Lendo Företagslån */}
            <div className="w-full lg:max-w-sm">
          <DynamicDetailedLoanCard
            provider="Lendo"
            logo={!logosLoading ? logos.lendo : "/placeholder.svg"}
            loanAmount="50 000 – 5 000 000 kr"
            nominalRate="Varierar mellan långivare"
            effectiveRate="Varierar mellan långivare"
            loanTerm="6 – 60 mån"
            loanWithoutUC="Nej"
            responseTime="Inom 24h"
            paymentSolution="Banköverföring"
            link={`/${currentLanguage}/foretagslan/lendo`}
            lenderBadge="Jämför 15 långivare"
            entityType="foretagslan"
            entitySlug="lendo"
            fallbackRating={4.4}
            fallbackReviewCount={3373}
            liveTermsOverride={liveTermsByBusinessSlug.lendo ?? null}
          />
            </div>

            {/* 3. Froda */}
            <div className="w-full lg:max-w-sm">
          <DynamicDetailedLoanCard
            provider="Froda"
            logo={!logosLoading ? logos.froda : "/placeholder.svg"}
            loanAmount="Upp till 5 000 000 kr"
            nominalRate="1,00 – 2,17 % mån"
            effectiveRate="12,7 – 29,3 %"
            loanTerm="Flexibel"
            loanWithoutUC="Ja"
            responseTime="Direkt"
            paymentSolution="Banköverföring"
            link={`/${currentLanguage}/foretagslan/froda`}
            entityType="foretagslan"
            entitySlug="froda"
            fallbackRating={4.9}
            fallbackReviewCount={1668}
            liveTermsOverride={liveTermsByBusinessSlug.froda ?? null}
          />
            </div>

            {/* 4. Qred */}
            <div className="w-full lg:max-w-sm">
          <DynamicDetailedLoanCard
            provider="Qred"
            logo={!logosLoading ? logos.qred : "/placeholder.svg"}
            loanAmount="Upp till 5 000 000 kr"
            nominalRate="1,50 – 3,10 % mån"
            effectiveRate="19,6 – 43,3 %"
            loanTerm="Flexibel"
            loanWithoutUC="Nej"
            responseTime="Inom 1 timme"
            paymentSolution="Banköverföring"
            link={`/${currentLanguage}/foretagslan/qred`}
            entityType="foretagslan"
            entitySlug="qred"
            fallbackRating={4.7}
            fallbackReviewCount={1092}
            liveTermsOverride={liveTermsByBusinessSlug.qred ?? null}
          />
            </div>

            {/* 5. Fedelta */}
            <div className="w-full lg:max-w-sm">
          <DynamicDetailedLoanCard
            provider="Fedelta"
            logo={!logosLoading ? logos.fedelta : "/placeholder.svg"}
            loanAmount="50 000 – 5 000 000 kr"
            nominalRate="0,90 – 1,40 % mån"
            effectiveRate="11,4 – 18,1 %"
            loanTerm="6 – 60 mån"
            loanWithoutUC="Nej"
            responseTime="Inom 24h"
            paymentSolution="Banköverföring"
            link={`/${currentLanguage}/foretagslan/fedelta`}
            entityType="foretagslan"
            entitySlug="fedelta"
            fallbackRating={4.8}
            fallbackReviewCount={135}
            liveTermsOverride={liveTermsByBusinessSlug.fedelta ?? null}
          />
            </div>

            {/* 6. OPR Företagslån */}
            <div className="w-full lg:max-w-sm">
          <DynamicDetailedLoanCard
            provider="OPR Företagslån"
            logo={!logosLoading ? logos.opr : "/placeholder.svg"}
            loanAmount="Upp till 1 000 000 kr"
            nominalRate="1,70 – 2,30 % mån"
            effectiveRate="22,4 – 31,2 %"
            loanTerm="Flexibel"
            loanWithoutUC="Ja"
            responseTime="1-2 dagar"
            paymentSolution="Banköverföring"
            link={`/${currentLanguage}/foretagslan/opr-foretagslan`}
            entityType="foretagslan"
            entitySlug="opr-foretagslan"
            fallbackRating={4.6}
            fallbackReviewCount={351}
            liveTermsOverride={liveTermsByBusinessSlug["opr-foretagslan"] ?? null}
          />
            </div>
          </div>
        </div>
      </section>

      <ComparisonMethodology
        title="Så jämför vi företagslån"
        description="Företagslån behöver bedömas efter företagets situation, inte bara efter ränta. Kassaflöde, säkerhet, löptid och krav på firmatecknare påverkar den verkliga kostnaden."
        items={[
          {
            title: "Belopp och återbetalning matchas mot behov",
            body: "Ett rörelsekapitallån, en investering och en tillfällig likviditetslösning ställer olika krav på löptid och amortering.",
          },
          {
            title: "Förmedlare gör jämförelsen bredare",
            body: "En förmedlare kan ge flera erbjudanden på en ansökan. En enskild långivare kan vara snabbare men visar bara sitt eget villkor.",
          },
          {
            title: "Företagsdata måste vara kontrollerad",
            body: "Räntor, avgifter, krav på omsättning och eventuell personlig borgen ska granskas innan de används i rekommendationer.",
          },
        ]}
      />

      <DecisionPathLinks
        title="Nästa steg för företagets finansiering"
        description="Besökaren ska snabbt kunna gå från generell jämförelse till rätt kalkyl, guide eller långivarsida beroende på företagets behov."
        links={[
          {
            title: "Jämför företagslån",
            description: "Gå direkt till listan med låneförmedlare och företagslångivare.",
            href: "#compare-loans-section",
            label: "Till jämförelsen",
            badge: "Huvudflöde",
          },
          {
            title: "Räkna på kostnaden",
            description: "Beräkna ungefärlig månadsbetalning och effektiv ränta för företagslånet.",
            href: "#business-loan-calculator",
            label: "Öppna kalkylatorn",
          },
          {
            title: "Företagslån för AB",
            description: "Se vad som gäller för aktiebolag, firmatecknare och ansvar.",
            href: `/${currentLanguage}/guider/foretagslan-aktiebolag`,
            label: "Läs guiden",
          },
          {
            title: "Krea företagslån",
            description: "Läs mer om förmedlaren som jämför flera företagslångivare.",
            href: `/${currentLanguage}/foretagslan/krea`,
            label: "Till Krea-sidan",
          },
          {
            title: "Personlig borgen",
            description: "Förstå när ägare eller firmatecknare kan bli personligt ansvariga.",
            href: `/${currentLanguage}/guider/borgensman`,
            label: "Läs mer",
          },
          {
            title: "Kreditupplysning",
            description: "Se hur företagets och företrädarens kreditvärdighet kan påverka lånet.",
            href: `/${currentLanguage}/guider/kreditupplysning`,
            label: "Förstå kraven",
          },
        ]}
      />

      {/* Säkerhet och borgen */}
      <section className="pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-12 lg:pb-16">
        <div className="container max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Kräver företagslån säkerhet?</h2>

          <p className="text-muted-foreground text-lg mb-6">
            Majoriteten av de banker och långivare vi samarbetar med kräver personlig borgen i samband med lån till företag. Det här är ingen säkerhet i egentlig mening utan innebär att en eller flera i bolaget åtar sig att betala av lånet om företaget inte kan betala av skulden enligt överenskommelse. Vid större lån, mellan 500 000 - 5 000 000 kr kan en säkerhet i form av företagsinteckning krävas. Vid dessa belopp brukar det även finnas möjlighet att använda fastigheten som pant.
          </p>

          <div className="prose prose-lg max-w-none">

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Vanliga säkerhetsformer</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-1 shrink-0" />
                    <div>
                      <strong>Företagsinteckning</strong> – Pant i företagets tillgångar
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-1 shrink-0" />
                    <div>
                      <strong>Fastighet</strong> – Om företaget äger fastigheter
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-1 shrink-0" />
                    <div>
                      <strong>Personlig borgen</strong> – Du som ägare går i borgen privat
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-1 shrink-0" />
                    <div>
                      <strong>Aktier och värdepapper</strong> – Noterade värdepapper
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Alert className="border-success/50 bg-success/10 mt-6">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <AlertDescription>
                <strong>Tips:</strong> Chanserna att få lägre ränta och bättre villkor är betydligt större när du lånar
                med säkerhet, eftersom risken för långivaren minskar.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </section>

      {/* Vilka företag kan ansöka */}
      <section className="pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-12 lg:pb-16 bg-muted/30">
        <div className="container max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Vilka företag kan ansöka om företagslån?</h2>

          <p className="text-muted-foreground text-lg mb-6">
            De flesta företag kan ansöka om företagslån, men förutsättningarna varierar mellan olika bolagsformer. Den
            främsta skillnaden är hur stort personligt ansvar du som företagsägare har för lånet.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-3">Aktiebolag (AB)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Ett aktiebolag är en egen juridisk person. Betalningskrav kan därför endast ställas mot företaget,
                  inte mot dig som privatperson.
                </p>
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
                  <span>Begränsat personligt ansvar (om ingen borgen)</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-3">Enskild firma</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Du själv representerar företaget och är därför personligt ansvarig för företagets ekonomi. Om
                  företaget inte kan betala lånet måste du göra det med dina privata medel.
                </p>
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>Fullt personligt ansvar</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-3">Handelsbolag (HB)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Ägarna är mer personligt ansvariga än hos aktiebolag. Om företaget går i konkurs tillfaller skulden
                  ägarna även efter att bolaget upphört.
                </p>
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>Solidariskt ansvar mellan delägare</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-3">Kommanditbolag (KB)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Likt handelsbolag, men minst en ägare kan ha begränsat ansvar medan en eller flera andra ägare har
                  fullt ansvar för företagets skulder.
                </p>
                <div className="flex items-start gap-2 text-sm">
                  <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>Delat ansvar mellan komplementärer och kommanditdelägare</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Alert className="border-primary/50 bg-primary/5 mt-6">
            <Info className="h-5 w-5 text-primary" />
            <AlertDescription>
              <strong>För nystartade företag:</strong> Mindre företag och nystartade bolag kan också få företagslån, men
              chanserna är större hos långivare som specialiserar sig på småföretag och startup-finansiering.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* Ansökningsprocess och krav */}
      <section className="pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-12 lg:pb-16">
        <div className="container max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Så ansöker du om företagslån</h2>

          <div className="space-y-4 mb-8">
            {[
              {
                step: "1",
                title: "Förbered dokumentation",
                description: "Samla bokslut, budget, företagsinformation och ägarstruktur",
              },
              {
                step: "2",
                title: "Ansök via långivare eller förmedlare",
                description: "Fyll i ansökan med företagsuppgifter och lånebelopp",
              },
              {
                step: "3",
                title: "Kreditprövning genomförs",
                description: "Långivaren bedömer företagets ekonomi och kreditvärdighet",
              },
              {
                step: "4",
                title: "Få låneerbjudande",
                description: "Granska villkor, ränta och återbetalningstid",
              },
              {
                step: "5",
                title: "Signera avtal",
                description: "Firmatecknare skriver under låneavtalet",
              },
              {
                step: "6",
                title: "Utbetalning",
                description: "Pengarna betalas ut till företagskontot (vanligtvis inom 1-3 bankdagar)",
              },
            ].map((item, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold mb-4">Generella ansökningskrav</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Aktivt företag registrerat i Sverige</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Minst 3 månaders verksamhet (ofta)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Svenskt företagskonto</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Budget och affärsplan</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Dokumentation om ägarstruktur</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">Firmatecknare med behörighet</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Kostnader och räntor */}
      <section className="pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-12 lg:pb-16 bg-muted/30">
        <div className="container max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Vad kostar ett företagslån?</h2>

          <p className="text-muted-foreground text-lg mb-6">
            Kostnaden för ett företagslån består vanligtvis av ränta och eventuella avgifter. Räntan för företagslån
            varierar normalt mellan 10-30% per år och sätts utifrån företagets försäljning, kreditbetyg och ekonomiska
            situation.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Räntekostnader</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Nominell ränta:</strong> Den årliga räntesatsen
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Effektiv ränta:</strong> Total kostnad inkl. avgifter
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Fast eller rörlig:</strong> Beror på långivare och lånetyp
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Övriga avgifter</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Uppläggningsavgift:</strong> Varierar mellan långivare, många erbjuder 0 kr
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Fakturaavgift:</strong> Vanligtvis 0 kr hos våra samarbetspartners
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>
                      <strong>Förtidslösen:</strong> Kan tillkomma vid förtida betalning
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold mb-4">Exempel: Företagslån 100 000 kr</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span>Lånebelopp:</span>
                  <strong>100 000 kr</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span>Återbetalningstid:</span>
                  <strong>12 månader</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span>Månadskostnad:</span>
                  <strong>ca 9 200 kr/mån</strong>
                </div>
                <div className="flex justify-between py-2 font-bold">
                  <span>Totalt att betala tillbaka:</span>
                  <strong>ca 110 400 kr</strong>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                * Exempel baserat på månadsränta 1,5% (ca 19,6% effektiv årsränta). Faktisk kostnad varierar beroende på företag och långivare.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Loan Calculator */}
      <section id="business-loan-calculator" className="pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-12 lg:pb-16 bg-background">
        <div className="container max-w-6xl px-3 md:px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Kostnadskalkyl för företagslån
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Beräkna totalkostnaden och den effektiva årsräntan för ditt företagslån. 
              Fyll i lånebelopp, månadsränta och återbetalningstid så räknar vi ut resten.
            </p>
          </div>
          
          <Suspense fallback={<CalculatorSkeleton variant="detailed" />}>
            <BusinessLoanCalculator defaultAmount={100000} />
          </Suspense>
        </div>
      </section>

      {/* Faktorer som påverkar */}
      <section className="py-8 md:py-12 lg:py-16">
        <div className="container max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Faktorer som påverkar räntan</h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Företagets ålder och historik",
              "Omsättning och lönsamhet",
              "Kreditupplysning och rating",
              "Eventuell säkerhet för lånet",
              "Styrelsemedlemmarnas erfarenhet",
              "Borgensmannens ekonomi",
              "Affärsplan och framtidsutsikter",
              "Bransch och marknadssituation",
              "Lånebelopp och återbetalningstid",
            ].map((factor, i) => (
              <Card key={i} className="hover:border-primary/50 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm font-medium">{factor}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Alert className="border-success/50 bg-success/10 mt-6">
            <CheckCircle2 className="h-5 w-5 text-success" />
            <AlertDescription>
              <strong>Tips för bättre villkor:</strong> Använd en låneförmedlare för att jämföra flera erbjudanden
              samtidigt utan att göra flera kreditupplysningar, vilket kan påverka kreditvärdigheten negativt.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Vanliga frågor om företagslån
          </h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left">
                Kan man få företagslån trots betalningsanmärkning?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p className="mb-4">
                  Ja, vissa långivare kan bevilja lån trots att företaget eller firmatecknaren har
                  betalningsanmärkningar. Det förutsätter dock att företagets kreditvärdighet uppfyller kraven i övrigt
                  och att de ekonomiska förutsättningarna ser bra ut.
                </p>
                <Alert className="border-destructive/30 bg-destructive/10 flex items-start gap-3">
                  <AlertDescription className="text-sm flex-1">
                    Ju fler och nyare anmärkningar, desto svårare blir det. Många långivare har gränser för hur många
                    anmärkningar som accepteras.
                  </AlertDescription>
                </Alert>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left">
                Vad är personlig borgen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Personlig borgen innebär att du som privatperson går i borgen för företagets lån. Om företaget inte
                  kan betala tillbaka lånet blir du personligt betalningsansvarig med dina privata tillgångar. Detta är
                  vanligt för mindre företagslån och blancolån där ingen annan säkerhet ställs.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left">
                Hur svårt är det att få företagslån?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Det varierar kraftigt beroende på företagets situation. Etablerade företag med god ekonomi och flera
                  års bokslut har lättare att få lån. Nystartade företag har det ofta svårare, men det finns långivare
                  som specialiserar sig på startup-finansiering och accepterar kortare historik.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left">
                Kan man ta företagslån utan UC?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, det går att ansöka om företagslån utan UC. De flesta långivare gör en kreditupplysning, men vissa
                  använder andra kreditupplysningsföretag som Bisnode eller Creditsafe i stället för UC. Det innebär att
                  din kreditvärdighet inte påverkas hos UC, vilket kan vara en fördel om du vill undvika flera
                  förfrågningar där.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left">
                Kan jag få företagslån som enskild firma?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, en enskild firma kan ansöka om företagslån, men långivare kommer att titta på din personliga 
                  kreditvärdighet samt företags ekonomi. Du behöver sannolikt gå i personlig borgen vid lån utan säkerhet.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger className="text-left">
                Vad är skillnaden mellan företagslån och factoring?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Företagslån är ett lån som du får för att finansiera verksamhetens behov, medan factoring är en 
                  finansieringslösning där du säljer dina kundfordringar till en tredje part för att få snabbare 
                  tillgång till kapital.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger className="text-left">
                Kan man ta företagslån utan säkerhet?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, det finns möjlighet att få ett företagslån utan säkerhet, men räntorna kan då vara högre och 
                  lånebeloppet kan vara mindre. Alternativa långivare erbjuder ofta lån utan säkerhet, men det ställs 
                  då högre krav på din kreditvärdighet.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <ReviewedBy variant="default" />

      {/* CTA Section - Knappar längst ner */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-primary/5 via-background to-primary/5">
        <div className="container max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Redo att jämföra företagslån?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Hitta det bästa företagslånet för ditt företag. Jämför räntor, villkor och få svar snabbt.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Grön knapp - scroll till kort-sektion */}
            <Button asChild size="lg" className="min-w-[200px]">
              <a href="#compare-loans-section">
                Jämför företagslån
              </a>
            </Button>

            {/* Vita guide-knappar */}
            <Button asChild size="lg" variant="outline" className="min-w-[200px]">
              <Link to={`/${currentLanguage}/guider/foretagslan-aktiebolag`}>
                <BookOpen className="mr-2 h-5 w-5" />
                Företagslån för AB
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="min-w-[200px]">
              <Link to={`/${currentLanguage}/guider/kreditupplysning`}>
                <BookOpen className="mr-2 h-5 w-5" />
                Om kreditupplysning
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      <StickyCTA show={showStickyCTA}>
        <div className="container mx-auto max-w-5xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">Krea</p>
              <p className="truncate text-xs text-muted-foreground">Jämför 30+ långivare — kostnadsfritt</p>
            </div>
            <Button asChild size="lg" className="whitespace-nowrap bg-primary text-white hover:bg-primary/90">
              <a
                href="https://go.adt253.net/t/t?a=1301012751&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
              >
                Ansök hos Krea
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </StickyCTA>
    </div>
  );
};
export default BusinessLoanPage;
